import React from "react";
import { useTranslation } from "react-i18next";
import { Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@shadcn-in-nx/ui/button";
import CodeScanExplainer from "./CodeScanExplainer";
import CodeScanFindingsList from "./CodeScanFindingsList";

function CodeScanStatusDisplay({ scanResult, isScanning, scanTimedOut, findings, onUpdateStatus, onRefresh }) {
    const { t } = useTranslation();

    if (scanTimedOut) {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
                <AlertTriangle className="w-12 h-12 text-amber-500" />
                <div className="text-center">
                    <p className="text-sm font-medium text-gray-900">
                        {t("appPreview.scanTimedOut")}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                        {t("appPreview.scanTimedOutDescription")}
                    </p>
                </div>
                <Button size="sm" variant="outline" onClick={onRefresh}>
                    <RefreshCw className="w-4 h-4 mr-1" />
                    {t("appPreview.refreshStatus")}
                </Button>
            </div>
        );
    }

    if (scanResult?.status === "FAILED") {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
                <AlertTriangle className="w-12 h-12 text-red-500" />
                <div className="text-center">
                    <p className="text-sm font-medium text-gray-900">
                        {t("appPreview.scanFailedTitle")}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                        {scanResult.error || t("appPreview.scanFailedDescription")}
                    </p>
                </div>
            </div>
        );
    }

    if (!scanResult && !isScanning) {
        return <CodeScanExplainer />;
    }

    if (isScanning || (scanResult && ["PENDING", "RUNNING"].includes(scanResult.status))) {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
                <div className="flex items-center justify-center">
                    <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
                </div>
                <div className="text-center">
                    <p className="text-sm font-medium text-gray-900">
                        {t("appPreview.scanningInProgress")}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                        {t("appPreview.scanningDescription")}
                    </p>
                </div>
            </div>
        );
    }

    if (scanResult?.status === "COMPLETED" && findings.length === 0) {
        return <CodeScanExplainer hasScanned />;
    }

    if (findings.length > 0) {
        return <CodeScanFindingsList findings={findings} onUpdateStatus={onUpdateStatus} />;
    }

    return null;
}

export default React.memo(CodeScanStatusDisplay);
